'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { Check, Copy, Globe2, Plus, RotateCcw, Trash2, TriangleAlert } from 'lucide-react';
import type { Locale } from '@/lib/content';
import type { ToolContent } from '@/lib/tools/types';
import { logToolUsage } from '@/lib/tools/analytics';
import { ToolSignupWall } from './tool-signup-wall';
import {
  EU_VAT_RATES,
  OSS_THRESHOLD_EUR,
  RATES_VERIFIED_ON,
  countryName,
  euVatRate,
  vatOn,
} from '@/lib/tools/eu-vat-rates';

interface OssReportProps {
  locale: Locale;
  content: ToolContent;
}

interface SalesRow {
  id: number;
  country: string;
  netAmount: string;
  useReduced: boolean;
}

/**
 * The seller's own country. Sales at home are not OSS sales, so it is excluded
 * from the destination list rather than silently counted into the return.
 */
const HOME_COUNTRY_BY_LOCALE: Record<Locale, string> = { nl: 'NL', en: 'NL', de: 'DE', fr: 'FR' };

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'] as const;

/** OSS returns are due at the end of the month following the quarter. */
const QUARTER_DEADLINE: Record<(typeof QUARTERS)[number], string> = {
  Q1: '30-04',
  Q2: '31-07',
  Q3: '31-10',
  Q4: '31-01',
};

let nextRowId = 1;

export function OssReport({ locale, content }: OssReportProps) {
  const labels = content.labels ?? {};
  const home = HOME_COUNTRY_BY_LOCALE[locale] ?? 'NL';

  const [rows, setRows] = useState<SalesRow[]>([
    { id: nextRowId++, country: 'DE', netAmount: '', useReduced: false },
  ]);
  const [quarter, setQuarter] = useState<(typeof QUARTERS)[number]>('Q1');
  const [copied, setCopied] = useState(false);
  const hasLoggedRef = useRef(false);

  const destinations = useMemo(
    () =>
      EU_VAT_RATES.filter((r) => r.code !== home)
        .map((r) => ({ ...r, name: countryName(r.code, locale) }))
        .sort((a, b) => a.name.localeCompare(b.name, locale)),
    [home, locale]
  );

  const lines = useMemo(
    () =>
      rows.map((row) => {
        const rate = euVatRate(row.country);
        const net = parseFloat(row.netAmount.replace(',', '.'));
        const usableNet = Number.isFinite(net) && net > 0 ? net : 0;
        const percent = row.useReduced && rate?.reduced != null ? rate.reduced : rate?.standard ?? 0;

        return {
          ...row,
          name: countryName(row.country, locale),
          percent,
          net: usableNet,
          vat: vatOn(usableNet, percent),
        };
      }),
    [rows, locale]
  );

  const totals = useMemo(() => {
    const net = lines.reduce((sum, l) => sum + l.net, 0);
    const vat = lines.reduce((sum, l) => sum + l.vat, 0);
    return { net, vat, gross: net + vat };
  }, [lines]);

  const belowThreshold = totals.net > 0 && totals.net < OSS_THRESHOLD_EUR;

  useEffect(() => {
    if (!hasLoggedRef.current && totals.net > 0) {
      hasLoggedRef.current = true;
      logToolUsage({ tool_name: 'oss-report', locale });
    }
  }, [totals.net, locale]);

  const money = (value: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value);

  const addRow = () => {
    const used = new Set(rows.map((r) => r.country));
    const next = destinations.find((d) => !used.has(d.code)) ?? destinations[0];
    setRows((current) => [...current, { id: nextRowId++, country: next.code, netAmount: '', useReduced: false }]);
  };

  const updateRow = (id: number, patch: Partial<SalesRow>) =>
    setRows((current) => current.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const removeRow = (id: number) =>
    setRows((current) => (current.length === 1 ? current : current.filter((r) => r.id !== id)));

  const reset = () => {
    setRows([{ id: nextRowId++, country: 'DE', netAmount: '', useReduced: false }]);
    setQuarter('Q1');
  };

  const summary = () =>
    [
      `${labels.summaryHeading ?? 'OSS return'} ${quarter}`,
      ...lines
        .filter((l) => l.net > 0)
        .map((l) => `${l.name} (${l.country})\t${l.percent}%\t${l.net.toFixed(2)}\t${l.vat.toFixed(2)}`),
      `${labels.totalNet ?? 'Net'}\t${totals.net.toFixed(2)}`,
      `${labels.totalVat ?? 'VAT due'}\t${totals.vat.toFixed(2)}`,
    ].join('\n');

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(summary());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100">
          <Globe2 className="h-7 w-7 text-brand-700" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{content.title}</h2>
        <p className="mt-3 text-lg text-slate-600">{content.description}</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">{labels.quarter ?? 'Kwartaal'}</label>
          <div className="grid grid-cols-4 gap-2">
            {QUARTERS.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => setQuarter(q)}
                className={`rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                  quarter === q ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {q}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {labels.deadlineNote ?? 'Aangiftedeadline'}: {QUARTER_DEADLINE[quarter]}
          </p>
        </div>

        <div className="space-y-3">
          {lines.map((line) => (
            <div key={line.id} className="rounded-xl border border-slate-200 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={line.country}
                  onChange={(e) => updateRow(line.id, { country: e.target.value })}
                  aria-label={labels.country ?? 'Land'}
                  className="min-w-[9rem] flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                >
                  {destinations.map((d) => (
                    <option key={d.code} value={d.code}>
                      {d.name} ({d.code})
                    </option>
                  ))}
                </select>

                <input
                  inputMode="decimal"
                  value={line.netAmount}
                  onChange={(e) => updateRow(line.id, { netAmount: e.target.value })}
                  placeholder={labels.netAmount ?? 'Omzet excl. btw'}
                  aria-label={labels.netAmount ?? 'Omzet excl. btw'}
                  className="w-36 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900"
                />

                <button
                  type="button"
                  onClick={() => removeRow(line.id)}
                  disabled={rows.length === 1}
                  aria-label={labels.removeRow ?? 'Rij verwijderen'}
                  className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                <label className="flex items-center gap-2 text-slate-600">
                  <input
                    type="checkbox"
                    checked={line.useReduced}
                    disabled={euVatRate(line.country)?.reduced == null}
                    onChange={(e) => updateRow(line.id, { useReduced: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  {labels.reducedRate ?? 'Verlaagd tarief'}
                  {euVatRate(line.country)?.reduced == null && (
                    <span className="text-xs text-slate-400">({labels.noReducedRate ?? 'niet beschikbaar'})</span>
                  )}
                </label>
                <span className="font-medium text-slate-900">
                  {line.percent}% &middot; {money(line.vat)}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
          >
            <Plus className="h-4 w-4" />
            {labels.addCountry ?? 'Land toevoegen'}
          </button>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
          >
            <RotateCcw className="h-4 w-4" />
            {labels.reset ?? 'Reset'}
          </button>
        </div>

        <div className="mt-6 rounded-xl bg-slate-50 p-4">
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>{labels.totalNet ?? 'Totaal omzet excl. btw'}</span>
            <span className="font-medium text-slate-900">{money(totals.net)}</span>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">{labels.totalVat ?? 'Te betalen btw'}</span>
            <span className="text-xl font-semibold text-brand-700">{money(totals.vat)}</span>
          </div>
          <div className="mt-2 flex items-center justify-between text-sm text-slate-600">
            <span>{labels.totalGross ?? 'Totaal incl. btw'}</span>
            <span className="font-medium text-slate-900">{money(totals.gross)}</span>
          </div>

          <button
            type="button"
            onClick={copySummary}
            disabled={totals.net === 0}
            className="mt-4 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-200 disabled:opacity-40"
          >
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            {copied ? labels.copied ?? 'Gekopieerd' : labels.copySummary ?? 'Kopieer overzicht'}
          </button>
        </div>

        {belowThreshold && (
          <div className="mt-4 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <TriangleAlert className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
            <p className="text-sm leading-relaxed text-amber-900">
              {labels.thresholdNote ??
                'Je grensoverschrijdende omzet blijft onder de EU-drempel van € 10.000. Tot die grens mag je het btw-tarief van je eigen land blijven rekenen; daarboven is het tarief van het bestemmingsland verplicht.'}
            </p>
          </div>
        )}

        <p className="mt-4 text-xs text-slate-400">
          {labels.ratesVerified ?? 'Tarieven gecontroleerd op'} {RATES_VERIFIED_ON}
        </p>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-slate-800">{labels.automateHeading ?? 'Liever niet overtypen?'}</h3>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          {labels.automateBody ??
            'Winkel Factuur leest je bestellingen van bol.com, Amazon, Shopify en WooCommerce en zet deze tabel per kwartaal zelf klaar, inclusief het juiste tarief per land en per product.'}
        </p>
        <Link
          href={`/${locale}/signup/`}
          className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800"
          data-track="OSS Report Automate"
          data-track-category="CTA"
        >
          {labels.automateCta ?? 'Bekijk hoe dat werkt'}
        </Link>
      </div>

      <ToolSignupWall locale={locale} />

      {content.disclaimer && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="mb-2 text-sm font-semibold text-slate-700">{content.disclaimer.heading}</h3>
          <p className="text-xs leading-relaxed text-slate-500">{content.disclaimer.content}</p>
        </div>
      )}
    </div>
  );
}
